import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import cx from "classnames";
import useDeepCompareEffect from "use-deep-compare-effect";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlane, faTimes } from "@fortawesome/free-solid-svg-icons";
import { debounce } from "throttle-debounce";
import { AppContext } from "../../../context";
import { getGateways, getOneWayGateways } from "./utils/getGateways";
import getInitialFrom from "./utils/getInitialFrom";
import fromFieldAdapter from "./FromField.adapter";
import { InputWrapper } from "../../../components/InputWrapper";
import { Typeahead } from "../../../components/Typeahead";
import { BRANDS, MODES } from "../../../configs";
import {loadSOVGatewayData} from "../../Shared/utils/loadSOVGatewayData";
import {transformSOVGatewayData} from "../../Shared/utils/transformSOVGatewayData";
import { FLIGHTS_FROM_MULTIPLE_FIELD_ID_1 } from "../../../configs/const";

const FromField = ({
	id,
	mode,
	index,
	className,
	classnames,

	selectedType,
	selectedFrom,
	multipleFlightData,
	previousSessionValue,

	error,
	isOnlyOneTrue,

	resetError,
	onChange,
	handleFocus,
	setNextButtonDisabled,
	setMultipleFlightData,
	handleSetAvailableDates,
	handleSovMobileInputSelectionToView
}) => {
	const [useAppState] = AppContext.useAppContext();
	const [typeaheadData, setTypeaheadData] = useState([]);
	const [initialGateway, setInitialGateway] = useState(null);
	const [isLoading, setIsLoading] = useState(useAppState.brand !== BRANDS.SOV);
	const referenceElement = useRef(null);
	const [isHasSelectedItem, setIsHasSelectedItem] = useState(
		!!selectedFrom?.label ?? false
	);

	const handleSOVInput = debounce(200, async searchQuery => {
		if (useAppState.brand === BRANDS.SOV) {
			resetError();

			if (searchQuery) {
				const gateways = await loadSOVGatewayData(searchQuery, resetError);
				
				if (gateways?.length > 0) {
					setTypeaheadData(transformSOVGatewayData(gateways));
				}
			}
		}
	});

	useDeepCompareEffect(() => {
		if (selectedType && useAppState.brand !== BRANDS.SOV) {
			setIsLoading(true);
			let gatewayTripType;
			if (selectedType === "OW") {
				gatewayTripType = getOneWayGateways(
					useAppState.lang,
					useAppState.alias
				);
			} else {
				gatewayTripType = getGateways(useAppState.lang, useAppState.alias);
			}
			gatewayTripType
				.then(data => {
					const [defaultGateway, list] = fromFieldAdapter(
						data,
						useAppState.lang
					);
					setTypeaheadData(list);
					setInitialGateway(
						getInitialFrom(data, defaultGateway, previousSessionValue)
					);

					handleSetAvailableDates(data?.Options);
					setIsLoading(false);
				})
				.catch(err => console.error(err));
		}
	}, [useAppState, selectedType, previousSessionValue]);

	useEffect(() => {
		if (selectedFrom?.label && !useAppState.sovSimplifiedSearchBar) {
			setIsHasSelectedItem(true);
		}
	}, [selectedFrom?.label, useAppState.sovSimplifiedSearchBar]);

	const placeHolderAndLabel = useAppState.getDictionary("traveling-from");
	return (
		<InputWrapper
			id={id}
			ref={referenceElement}
			key={useAppState.lang}
			brand={useAppState.brand}
			className={className}
			helperText={useAppState.getDictionary("select-a-departure-city")}
			label={
				useAppState.brand === BRANDS.SOV
					? placeHolderAndLabel
					: useAppState.getDictionary("departing-from")
			}
			error={error}
			hasSelectedItem={
				useAppState.brand === BRANDS.SOV ? !!isHasSelectedItem : !!selectedFrom
			}>
			{({ isFocused, handleOnFocus, handleOnBlur }) => (
				<Typeahead
					id={id}
					mode={mode}
					index={index}
					brand={useAppState.brand}
					selectedType={selectedType}
					isFocused={isFocused}
					data={typeaheadData}
					loading={isLoading}
					isMobileSOV={useAppState.sovSimplifiedSearchBar}
					isHasSelectedItem={
						useAppState.brand === BRANDS.SOV
							? isHasSelectedItem
							: !!selectedFrom
					}
					handleSovMobileInputSelectionToView={
						handleSovMobileInputSelectionToView
					}
					placeholder={
						useAppState.brand === BRANDS.SOV ? placeHolderAndLabel : ""
					}
					initialSelectedItem={initialGateway}
					selectedItem={selectedFrom}
					multipleFlightData={multipleFlightData}
					error={error}
					setIsHasSelectedItem={setIsHasSelectedItem}
					onFocus={() => {
						resetError();
						handleOnFocus();
						handleFocus();
					}}
					onChange={value => {
						resetError();
						onChange?.(value);
						if(!value) {
							setNextButtonDisabled(true)
						}

						if(value?.label) {
							setNextButtonDisabled(false)
						}

						if (id === FLIGHTS_FROM_MULTIPLE_FIELD_ID_1) {
								setMultipleFlightData(prev => ({
									...prev,
									flight1: {
										...prev.flight1,
										selectedFrom: value,
									},
								}));
							}
					}}
					icon={
						useAppState.brand === BRANDS.SOV && (
							<span
								className={cx(classnames?.brand, {
									[classnames?.inputIcon]: useAppState.brand === BRANDS.SOV
								})}
								focus-trap-toggle="true">
								<FontAwesomeIcon
									icon={faPlane}
									className={classnames?.listViewIcon}
								/>
							</span>
						)
					}
					iconClose={
						(inputValue, reset) =>
							useAppState.brand === BRANDS.SOV &&
							useAppState.sovSimplifiedSearchBar &&
							isOnlyOneTrue &&
							inputValue && (
								<button
									type="button"
									onClick={() => {
										reset();
										setNextButtonDisabled(true)
									}}
									className={cx(classnames.sovCloseIcon, classnames.brand)}
									focus-trap-toggle="true">
									<FontAwesomeIcon
										icon={faTimes}
										className={classnames.listViewIcon}
									/>
								</button>
							)
						// eslint-disable-next-line react/jsx-curly-newline
					}
					showAllSuggestionsOnClickIcon={true}
					onInput={handleSOVInput}
					onBlur={handleOnBlur}
					environment={useAppState.reactDOM}
					labels={{
						noResults: useAppState.getDictionary("no-results-found")
					}}
				/>
			)}
		</InputWrapper>
	);
};

FromField.propTypes = {
	id: PropTypes.string,
	mode: PropTypes.string,
	index: PropTypes.number,
	className: PropTypes.string,
	selectedType: PropTypes.string,

	error: PropTypes.bool,
	isOnlyOneTrue: PropTypes.bool,

	classnames: PropTypes.shape({
		brand: PropTypes.string,
		inputIcon: PropTypes.string,
		listViewIcon: PropTypes.string,
		sovCloseIcon: PropTypes.string
	}),
	previousSessionValue: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),
	selectedFrom: PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string
	}),
	multipleFlightData: PropTypes.shape({
		flight1: PropTypes.shape({
			selectedFrom: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string
			}),
			selectedTo: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string
			}),
			selectedDates: PropTypes.oneOfType([
				PropTypes.arrayOf(PropTypes.instanceOf(dayjs)),
				PropTypes.arrayOf(PropTypes.string),
				PropTypes.instanceOf(dayjs),
				PropTypes.string
			])
		}),
		flight2: PropTypes.shape({
			selectedFrom: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string
			}),
			selectedTo: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string
			}),
			selectedDates: PropTypes.oneOfType([
				PropTypes.arrayOf(PropTypes.instanceOf(dayjs)),
				PropTypes.arrayOf(PropTypes.string),
				PropTypes.instanceOf(dayjs),
				PropTypes.string
			])
		}),
		flight3: PropTypes.shape({
			selectedFrom: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string
			}),
			selectedTo: PropTypes.shape({
				label: PropTypes.string,
				value: PropTypes.string
			}),
			selectedDates: PropTypes.oneOfType([
				PropTypes.arrayOf(PropTypes.instanceOf(dayjs)),
				PropTypes.arrayOf(PropTypes.string),
				PropTypes.instanceOf(dayjs),
				PropTypes.string
			])
		})
	}),

	resetError: PropTypes.func,
	handleFocus: PropTypes.func,
	onChange: PropTypes.func.isRequired,
	setNextButtonDisabled: PropTypes.func,
	setMultipleFlightData: PropTypes.func,
	handleSetAvailableDates: PropTypes.func.isRequired,
	handleSovMobileInputSelectionToView: PropTypes.func,
};

FromField.defaultProps = {
	id: undefined,
	mode: MODES.SEARCH,
	index: undefined,
	className: undefined,
	classnames: undefined,
	selectedType: undefined,

	error: false,
	isOnlyOneTrue: false,

	previousSessionValue: undefined,
	selectedFrom: undefined,
	multipleFlightData: undefined,

	resetError: () => {},
	handleFocus: () => {},
	setNextButtonDisabled: () => {},
	setMultipleFlightData: () => {},
	handleSovMobileInputSelectionToView: () => {},
};

export default FromField;
export { FromField };
